import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, ArrowUpRight } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

const links = [
  { href: "#tjanster", label: "Tjänster", testId: "nav-link-services" },
  { href: "#process", label: "Så fungerar det", testId: "nav-link-process" },
  { href: "#boka", label: "Boka tid", testId: "nav-link-booking" },
  { href: "#faq", label: "Vanliga frågor", testId: "nav-link-faq" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/75 border-b border-slate-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1360px] mx-auto px-5 lg:px-10 h-[108px] sm:h-[124px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-brand-logo"
          className="flex items-center shrink-0"
          aria-label="ZAM Autoverkstad"
        >
          <img
            src="/zam-logo.webp"
            alt="ZAM Autoverkstad"
            className="h-[104px] sm:h-[116px] w-auto object-contain"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testId}
              className="text-[0.92rem] text-slate-700 hover:text-slate-900 font-medium transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-[#D52027] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#boka"
            data-testid="nav-cta-book"
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-slate-900 text-white text-[0.85rem] font-semibold hover:bg-[#1F3C88] transition-colors"
          >
            Boka tid
            <ArrowUpRight className="h-4 w-4" />
          </a>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-menu"
                className="lg:hidden h-10 w-10 rounded-full border border-slate-200 bg-white grid place-items-center hover:bg-slate-50 transition"
                aria-label="Öppna meny"
              >
                <Menu className="h-5 w-5 text-slate-900" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] p-0 bg-white">
              <SheetTitle className="sr-only">Meny</SheetTitle>
              <SheetDescription className="sr-only">
                Navigation för ZAM Autoverkstad
              </SheetDescription>
              <div className="p-6 border-b border-slate-200">
                <img
                  src="/zam-logo.webp"
                  alt="ZAM Autoverkstad"
                  className="h-20 w-auto object-contain"
                />
              </div>
              <div className="p-6 flex flex-col gap-1">
                {links.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="py-3 border-b border-slate-100 text-slate-900 font-display text-[1.6rem] tracking-tight"
                  >
                    {l.label}
                  </a>
                ))}
                <a
                  href="#boka"
                  onClick={() => setOpen(false)}
                  className="mt-6 inline-flex items-center justify-center gap-2 py-3 rounded-full bg-[#D52027] text-white font-semibold"
                >
                  Boka verkstadsbesök
                  <ArrowUpRight className="h-4 w-4" />
                </a>
                <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                  Konceptdemo för ZAM Autoverkstad. Bokningsflödet kopplas in vid en
                  skarp lansering.
                </p>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
}
